/**
 * Zustand store for the Chat Agents feature.
 * Manages agent list, current chat state, and LibreChat auth tokens.
 */

import { create } from 'zustand';
import type {
  ChatAgent,
  ChatMessage,
  ConversationSummary,
  ToolCallEvent,
} from '@/types/chat-agent-types';

export interface ActiveToolCall {
  name: string;
  cleanName: string;
  args: string;
  startTime: number; // Date.now()
}

interface ChatAgentsState {
  // Agent list
  agents: ChatAgent[];
  isLoadingAgents: boolean;
  setAgents: (agents: ChatAgent[]) => void;
  setLoadingAgents: (loading: boolean) => void;

  // Current agent / chat
  currentAgent: ChatAgent | null;
  setCurrentAgent: (agent: ChatAgent | null) => void;

  // Messages
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  updateLastMessage: (content: string) => void;
  setMessageDone: (messageId: string) => void;
  clearMessages: () => void;

  // Tool-call rollup on the streaming assistant message
  addToolCallToLastMessage: (event: ToolCallEvent) => void;
  resolveToolCallOnLastMessage: (
    name: string,
    resolution: { summary?: string; url?: string; failed?: boolean },
  ) => void;
  settleRunningToolCalls: (status?: 'completed' | 'failed') => void;

  // Streaming state
  isStreaming: boolean;
  setStreaming: (streaming: boolean) => void;

  // Active tool call (for progress indicator)
  activeToolCall: ActiveToolCall | null;
  setActiveToolCall: (tool: ActiveToolCall | null) => void;

  // Conversation tracking
  conversationId: string | null;
  parentMessageId: string | null;
  setConversationId: (id: string | null) => void;
  setParentMessageId: (id: string | null) => void;

  // Conversation history sidebar
  conversations: ConversationSummary[];
  setConversations: (convos: ConversationSummary[]) => void;
  removeConversation: (conversationId: string) => void;
  isHistoryOpen: boolean;
  setHistoryOpen: (open: boolean) => void;

  // LibreChat auth
  libreChatToken: string | null;
  libreChatUrl: string | null;
  setLibreChatAuth: (token: string, url: string) => void;
}

export const useChatAgentsStore = create<ChatAgentsState>((set) => ({
  // Agent list
  agents: [],
  isLoadingAgents: false,
  setAgents: (agents) => set({ agents }),
  setLoadingAgents: (loading) => set({ isLoadingAgents: loading }),

  // Current agent
  currentAgent: null,
  setCurrentAgent: (agent) => set({ currentAgent: agent }),

  // Messages
  messages: [],
  addMessage: (message) =>
    set((state) => ({ messages: [...state.messages, message] })),
  updateLastMessage: (content) =>
    set((state) => {
      const messages = [...state.messages];
      const lastMsg = messages[messages.length - 1];
      if (lastMsg && lastMsg.role === 'assistant') {
        messages[messages.length - 1] = {
          ...lastMsg,
          content: lastMsg.content + content,
        };
      }
      return { messages };
    }),
  setMessageDone: (messageId) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, isStreaming: false } : m,
      ),
    })),
  clearMessages: () =>
    set({ messages: [], conversationId: null, parentMessageId: null }),

  // Tool-call rollup
  addToolCallToLastMessage: (event) =>
    set((state) => {
      const messages = [...state.messages];
      const lastMsg = messages[messages.length - 1];
      if (lastMsg && lastMsg.role === 'assistant') {
        messages[messages.length - 1] = {
          ...lastMsg,
          toolCalls: [...(lastMsg.toolCalls ?? []), event],
        };
      }
      return { messages };
    }),
  resolveToolCallOnLastMessage: (name, { summary, url, failed }) =>
    set((state) => {
      const messages = [...state.messages];
      const lastMsg = messages[messages.length - 1];
      if (!lastMsg || lastMsg.role !== 'assistant' || !lastMsg.toolCalls) {
        return { messages };
      }
      const toolCalls = [...lastMsg.toolCalls];
      // Resolve the most recent running call with a matching name, or the
      // most recent running call as a fallback.
      let idx = -1;
      let lastRunning = -1;
      for (let i = toolCalls.length - 1; i >= 0; i--) {
        if (toolCalls[i].status !== 'running') continue;
        if (lastRunning === -1) lastRunning = i;
        if (toolCalls[i].name === name) {
          idx = i;
          break;
        }
      }
      if (idx === -1) idx = lastRunning;
      if (idx === -1) return { messages };
      toolCalls[idx] = {
        ...toolCalls[idx],
        status: failed ? 'failed' : 'completed',
        ...(summary ? { summary } : {}),
        ...(url ? { url } : {}),
      };
      messages[messages.length - 1] = { ...lastMsg, toolCalls };
      return { messages };
    }),

  settleRunningToolCalls: (status = 'completed') =>
    set((state) => {
      const messages = [...state.messages];
      const lastMsg = messages[messages.length - 1];
      if (!lastMsg || lastMsg.role !== 'assistant' || !lastMsg.toolCalls) {
        return { messages };
      }
      if (!lastMsg.toolCalls.some((c) => c.status === 'running')) {
        return { messages };
      }
      const toolCalls = lastMsg.toolCalls.map((call) =>
        call.status === 'running' ? { ...call, status } : call,
      );
      messages[messages.length - 1] = { ...lastMsg, toolCalls };
      return { messages };
    }),

  // Streaming
  isStreaming: false,
  setStreaming: (streaming) => set({ isStreaming: streaming }),

  // Active tool call
  activeToolCall: null,
  setActiveToolCall: (tool) => set({ activeToolCall: tool }),

  // Conversation
  conversationId: null,
  parentMessageId: null,
  setConversationId: (id) => set({ conversationId: id }),
  setParentMessageId: (id) => set({ parentMessageId: id }),

  // Conversation history sidebar
  conversations: [],
  setConversations: (convos) => set({ conversations: convos }),
  removeConversation: (conversationId) =>
    set((state) => ({
      conversations: state.conversations.filter(
        (c) => c.conversationId !== conversationId,
      ),
    })),
  isHistoryOpen: false,
  setHistoryOpen: (open) => set({ isHistoryOpen: open }),

  // LibreChat auth
  libreChatToken: null,
  libreChatUrl: null,
  setLibreChatAuth: (token, url) =>
    set({ libreChatToken: token, libreChatUrl: url }),
}));
